/**
 * Dissociate a pair: reason, optional comment, and impact preview.
 */
import * as React from 'react';
import { Unlink, AlertTriangle } from 'lucide-react';
import { BaseModal } from '@/components/common/modals/BaseModal';
import { Button } from '@/components/forms/Button';
import { Select } from '@/components/forms/Select';
import { Textarea } from '@/components/forms/Textarea';
import {
  DISSOCIATION_IMPACTS,
  DISSOCIATION_IRREVERSIBLE_WARNING,
  DISSOCIATION_REASON_SELECT_OPTIONS,
} from '@/components/pairs/pair-configs';
import { useDissociatePair } from '@/hooks/usePairs';
import { useFormValidation } from '@/hooks/useFormValidation';
import { getErrorMessage } from '@/utils/errors-utils';
import { toast } from '@/components/ui/toast';
import type { DissociationReasonCode, PairRecord } from '@/types/remote/pair.types';
import { dissociationValidationSchema } from '@/utils/validation-schemas-utils';
import { getPairSwapLabel } from './pair-table-utils';
import {
  buildDissociatePairDto,
  dissociationRequiresDescription,
  formatFutureSessionsImpact,
} from './matching-utils';

/** Props for {@link DissociationModal}. */
interface DissociationModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly pair: PairRecord | null;
  readonly onSuccess?: () => void;
}

/** Confirm dissociation of a remote pair (reason + impacts). */
export function DissociationModal({ open, onOpenChange, pair, onSuccess }: DissociationModalProps) {
  const [selectedReason, setSelectedReason] = React.useState<DissociationReasonCode | ''>('');
  const [description, setDescription] = React.useState('');
  const validation = useFormValidation(dissociationValidationSchema);
  const dissociateMutation = useDissociatePair();
  const requiresDescription = dissociationRequiresDescription(selectedReason);
  const sessionsFuture = pair?.kpis?.sessionsFuture;

  /** Clear reason, comment, and validation when the modal closes. */
  function resetForm(): void {
    setSelectedReason('');
    setDescription('');
    validation.resetValidation();
  }

  function handleClose(): void {
    onOpenChange(false);
    resetForm();
  }

  function handleReasonChange(value: string): void {
    const nextReason = value as DissociationReasonCode;
    const next = { reason: nextReason, description };
    setSelectedReason(nextReason);
    validation.validateField('reason', nextReason, next);
    validation.markAsTouched('reason');
  }

  function handleDescriptionChange(event: React.ChangeEvent<HTMLTextAreaElement>): void {
    const nextDescription = event.target.value;
    const next = { reason: selectedReason, description: nextDescription };
    setDescription(nextDescription);
    validation.validateField('description', nextDescription, next);
    validation.markAsTouched('description');
  }

  async function handleSubmit(): Promise<void> {
    if (!pair || !selectedReason) return;
    if (!validation.validateForm({ reason: selectedReason, description })) return;

    try {
      await dissociateMutation.mutateAsync({
        pairId: pair.id,
        dto: buildDissociatePairDto(selectedReason, description),
      });

      onOpenChange(false);
      resetForm();
      onSuccess?.();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }

  /** One impact bullet from {@link DISSOCIATION_IMPACTS}. */
  function renderImpact(impact: string): React.ReactNode {
    return <li key={impact}>{impact}</li>;
  }

  /** Upcoming-session count next to the impacts title. */
  function renderFutureSessionsHint(): React.ReactNode {
    if (sessionsFuture === undefined) return null;
    return (
      <span className="font-normal text-muted-foreground">
        ({formatFutureSessionsImpact(sessionsFuture)})
      </span>
    );
  }

  /** Impact list + irreversible warning, shown once a reason is selected. */
  function renderImpacts(): React.ReactNode {
    if (!selectedReason) return null;
    return (
      <div
        className="rounded-xl border p-3.5 text-sm"
        style={{
          borderColor: 'color-mix(in srgb, rgb(var(--status-warning)) 30%, transparent)',
          background: 'color-mix(in srgb, rgb(var(--status-warning)) 6%, transparent)',
        }}
      >
        <p
          className="font-medium flex items-center gap-1.5"
          style={{ color: 'var(--color-1-nuance-1)' }}
        >
          <AlertTriangle
            className="w-4 h-4 shrink-0"
            style={{ color: 'rgb(var(--status-warning))' }}
          />
          Impacts de la dissociation
          {renderFutureSessionsHint()}
        </p>
        <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 pl-5 list-disc text-muted-foreground">
          {DISSOCIATION_IMPACTS.map(renderImpact)}
        </ul>
        <p
          className="mt-2.5 pt-2 border-t flex items-start gap-1.5 font-medium"
          style={{
            borderColor: 'color-mix(in srgb, rgb(var(--status-warning)) 25%, transparent)',
            color: 'rgb(var(--status-danger))',
          }}
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          {DISSOCIATION_IRREVERSIBLE_WARNING}
        </p>
      </div>
    );
  }

  if (!pair) return null;

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      size="xl"
      header={{
        preset: 'danger',
        icon: Unlink,
        iconSize: 'md',
        title: 'Dissocier le binôme',
        subtitle: getPairSwapLabel(pair),
        titleSize: 'lg',
        titleWeight: 'semibold',
      }}
      footer={
        <div className="flex gap-2 justify-end w-full">
          <Button
            preset="modal-cancel"
            onClick={handleClose}
            disabled={dissociateMutation.isPending}
          >
            Annuler
          </Button>
          <Button
            preset="modal-danger"
            onClick={handleSubmit}
            loading={dissociateMutation.isPending}
            disabled={!validation.isFormValid() || dissociateMutation.isPending}
          >
            Confirmer la dissociation
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Select
          label="Motif de dissociation"
          required
          placeholder="Sélectionnez un motif"
          value={selectedReason}
          onChange={handleReasonChange}
          disabled={dissociateMutation.isPending}
          options={DISSOCIATION_REASON_SELECT_OPTIONS}
          state={validation.getFieldState('reason')}
          error={validation.getFieldError('reason')}
        />

        {requiresDescription ? (
          <Textarea
            label="Commentaire"
            required
            placeholder="Précisez le motif..."
            value={description}
            onChange={handleDescriptionChange}
            rows={2}
            disabled={dissociateMutation.isPending}
            error={validation.getFieldError('description')}
          />
        ) : null}

        {renderImpacts()}
      </div>
    </BaseModal>
  );
}
